import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Settings2, Users } from "lucide-react";
import { useBillingTypeStats } from "@/hooks/useBillingTypeStats";

export const BillingTypeStatsCard = () => {
  const { stats, loading, error } = useBillingTypeStats();

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" />
            Configuración de Facturación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            Cargando estadísticas...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-primary" />
            Configuración de Facturación
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-destructive">
            Error al cargar estadísticas
          </div>
        </CardContent>
      </Card>
    );
  }

  const overridePercentage = stats.totalStudentActivities > 0 
    ? ((stats.withOverrides / stats.totalStudentActivities) * 100).toFixed(1)
    : '0';

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-primary" />
          Configuración de Facturación
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resumen general */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Total de inscripciones</span>
          </div>
          <Badge variant="outline" className="text-lg px-3 py-1">
            {stats.totalStudentActivities}
          </Badge>
        </div>

        {/* Estadísticas de configuración */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Configuración por defecto</span>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {stats.defaultBehavior}
              </Badge>
              <span className="text-xs text-muted-foreground">
                ({((stats.defaultBehavior / stats.totalStudentActivities) * 100).toFixed(1)}%)
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Configuración personalizada</span>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-primary text-primary">
                {stats.withOverrides}
              </Badge>
              <span className="text-xs text-muted-foreground">
                ({overridePercentage}%)
              </span>
            </div>
          </div>

          {stats.withOverrides > 0 && (
            <>
              <div className="pl-4 space-y-2 border-l-2 border-muted">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-3 h-3 text-blue-500" />
                    <span className="text-xs text-muted-foreground">Por día</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-blue-500 text-blue-700">
                    {stats.perSessionOverrides}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-3 h-3 text-purple-500" />
                    <span className="text-xs text-muted-foreground">Mensual</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-purple-500 text-purple-700">
                    {stats.monthlyOverrides}
                  </Badge>
                </div>
              </div>
            </>
          )}
        </div>

        {stats.withOverrides > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground">
              ⚡ {stats.withOverrides} estudiante{stats.withOverrides !== 1 ? 's' : ''} con facturación personalizada
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};